import { USERTYPE } from 'common/enum';
import Container from 'components/ui/Container';
import { useContext, useEffect } from 'react';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import { useNavigate } from 'react-router-dom';
import {
  useCurrentUserDetailsQuery,
  useListAdministratorQuery
} from 'redux/reducers/administrator';
import { useGetDashboardCount, useGetOngoingTraining } from 'redux/reducers/dashboard/dashboard.query';
import feedbackIcon from './icons/fluent.png';
import DashboardImage from './tmsDashboard.png';
import { ConfigurationContext } from 'components/provider/OrginazationContext';
import { backendURL } from 'redux/reducers/settings';
import { useCheckHasPermission } from 'utils/checkHaspermission';
import { ModuleCode } from 'utils/module.code.enum';
import { ActionType } from 'utils/action.type.enum';
import { HiOutlineUserGroup, HiOutlineUsers, HiOutlineCalendar, HiOutlineCheckCircle } from 'react-icons/hi';
import { MdOutlineGroups, MdOutlineDateRange, MdOutlineMeetingRoom } from 'react-icons/md';
import { TbInbox } from 'react-icons/tb';

interface ICoordinator {
  coordinator_details: {
    coordinator_id: number;
    first_name: string;
    last_name: string;
    middle_name: string;
  };
  is_module_coordinator: boolean;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { data: userDetails } = useCurrentUserDetailsQuery();
  const { data: adminData } = useListAdministratorQuery();

  const hasPermission = useCheckHasPermission(ModuleCode.DASHBOARD, ActionType?.CAN_VIEW)
  const { data: onGoingTrainings, isLoading: isTrainingsLoading } = useGetOngoingTraining('active', hasPermission);
  const { data: responseData } = useGetDashboardCount();

  const currentUserRole = adminData?.results?.find((item: any) => item.id === userDetails?.role);
  const isAdmin = 'Supervisor' === currentUserRole?.name;

  useEffect(() => {
    if (+userDetails?.data?.user_details?.user_type === USERTYPE.trainee && !userDetails?.data?.image) {
      navigate('/trainee/profile');
    }
  }, [userDetails, navigate]);
  const { appConfig } = useContext(ConfigurationContext)


  return (
    <div className="bg-light min-vh-100 pb-5">
      <style>{`
        .stat-card { transition: transform .2s ease, box-shadow .2s ease; }
        .stat-card:hover { transform: translateY(-4px); box-shadow: 0 .5rem 1.5rem rgba(0,0,0,.1) !important; }
        .card-hover-effect { transition: transform .2s ease, box-shadow .2s ease; }
        .card-hover-effect:hover { transform: translateY(-6px); box-shadow: 0 .75rem 2rem rgba(0,0,0,.12) !important; }
        .skeleton { position: relative; overflow: hidden; background: #e9ecef; border-radius: 6px; }
        .skeleton::after {
          content: ''; position: absolute; inset: 0;
          transform: translateX(-100%);
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.65), transparent);
          animation: skeleton-shimmer 1.4s infinite;
        }
        @keyframes skeleton-shimmer { 100% { transform: translateX(100%); } }
      `}</style>
      <Container title="Dashboard" subTitle="Training Management System">

        {/* Admin Hero Image */}
        {isAdmin && (
          <div className="row mb-4">
            <div className="col-12 text-center">
              <img src={DashboardImage} className="img-fluid rounded-4 shadow-sm" alt="Dashboard" style={{ maxHeight: '300px', width: '100%', objectFit: 'cover' }} />
            </div>
          </div>
        )}

        {/* --- STATS SECTION --- */}
        <div className="row g-3 mb-5">
          {/* Total Trainees */}
          <div className="col-md-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-4 stat-card position-relative overflow-hidden">
              <div className="card-body d-flex align-items-center gap-3 p-4">
                <div
                  className="d-flex align-items-center justify-content-center rounded-4 flex-shrink-0"
                  style={{ width: 56, height: 56, background: 'rgba(13,110,253,0.12)', color: '#0d6efd' }}>
                  <HiOutlineUserGroup size={28} />
                </div>
                <div>
                  <p className="text-muted mb-1 fw-semibold text-uppercase" style={{ fontSize: '11px', letterSpacing: '.5px' }}>Trainee Count</p>
                  <h3 className="mb-0 fw-bold">{responseData?.trainee_count || 0}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Internal/External */}
          <div className="col-md-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-4 stat-card">
              <div className="card-body p-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                    style={{ width: 36, height: 36, background: 'rgba(111,66,193,0.12)', color: '#6f42c1' }}>
                    <HiOutlineUsers size={20} />
                  </div>
                  <p className="text-muted mb-0 fw-semibold text-uppercase" style={{ fontSize: '11px', letterSpacing: '.5px' }}>User Segmentation</p>
                </div>
                <div className="d-flex justify-content-between text-center">
                  <div className="flex-fill">
                    <span className="d-block h4 mb-0 fw-bold text-primary">{responseData?.internal_user || 0}</span>
                    <span className="text-muted" style={{ fontSize: '12px' }}>Internal</span>
                  </div>
                  <div className="flex-fill border-start">
                    <span className="d-block h4 mb-0 fw-bold text-success">{responseData?.external_user || 0}</span>
                    <span className="text-muted" style={{ fontSize: '12px' }}>External</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Upcoming */}
          <div className="col-md-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-4 stat-card">
              <div className="card-body d-flex align-items-center gap-3 p-4">
                <div
                  className="d-flex align-items-center justify-content-center rounded-4 flex-shrink-0"
                  style={{ width: 56, height: 56, background: 'rgba(255,193,7,0.15)', color: '#f59e0b' }}>
                  <HiOutlineCalendar size={28} />
                </div>
                <div>
                  <p className="text-muted mb-1 fw-semibold text-uppercase" style={{ fontSize: '11px', letterSpacing: '.5px' }}>Upcoming</p>
                  <h3 className="mb-0 fw-bold">{responseData?.upcoming_training || 0}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Running/Completed */}
          <div className="col-md-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-4 stat-card">
              <div className="card-body d-flex align-items-center gap-3 p-4">
                <div
                  className="d-flex align-items-center justify-content-center rounded-4 flex-shrink-0"
                  style={{ width: 56, height: 56, background: 'rgba(25,135,84,0.12)', color: '#198754' }}>
                  <HiOutlineCheckCircle size={28} />
                </div>
                <div>
                  <p className="text-muted mb-1 fw-semibold text-uppercase" style={{ fontSize: '11px', letterSpacing: '.5px' }}>Completed Trainings</p>
                  <h3 className="mb-0 fw-bold">{responseData?.completed_training || 0}</h3>
                  <small className="text-success fw-semibold">
                    <span className="d-inline-block rounded-circle bg-success me-1" style={{ width: 7, height: 7 }}></span>
                    Running: {responseData?.running_training_count || 0}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center mb-4">
          <MdOutlineGroups className="text-primary me-2" size={24} />
          <h4 className="fw-bold mb-0 me-3">Ongoing Trainings</h4>
          {onGoingTrainings?.length ? (
            <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary me-3">
              {onGoingTrainings.length}
            </span>
          ) : null}
          <div className="flex-grow-1 border-bottom"></div>
        </div>

        {!isTrainingsLoading && onGoingTrainings && onGoingTrainings.length === 0 && (
          <div className="text-center text-muted py-5 my-3 bg-white rounded-4 shadow-sm">
            <TbInbox size={48} className="mb-3 opacity-50" />
            <p className="mb-0 fw-semibold">No ongoing trainings right now</p>
            <small>Active trainings will appear here once they begin.</small>
          </div>
        )}

        {/* Loading skeleton */}
        {isTrainingsLoading && (
          <div className="row g-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="col-sm-12 col-md-6 col-xl-4">
                <div className="card border-0 shadow-sm h-100 rounded-4 overflow-hidden">
                  <div className="skeleton" style={{ height: '110px', borderRadius: 0 }}></div>
                  <div className="card-body p-3">
                    <div className="skeleton mb-2" style={{ height: 18, width: '85%' }}></div>
                    <div className="skeleton mb-3" style={{ height: 18, width: '55%' }}></div>
                    <div className="skeleton mb-3" style={{ height: 28, width: 120, borderRadius: 999 }}></div>
                    <div className="skeleton mb-3" style={{ height: 52 }}></div>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1 me-2">
                        <div className="skeleton mb-2" style={{ height: 12, width: 80 }}></div>
                        <div className="skeleton mb-1" style={{ height: 12, width: '70%' }}></div>
                        <div className="skeleton" style={{ height: 12, width: '60%' }}></div>
                      </div>
                      <div className="skeleton rounded-circle flex-shrink-0" style={{ width: 34, height: 34 }}></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="row g-4">
          {onGoingTrainings?.map((routine: any, index: number) => (
            <div key={index} className="col-sm-12 col-md-6 col-xl-4">
              <div className="card border-0 shadow-sm h-100 rounded-4 overflow-hidden card-hover-effect">
                {/* Training Image Box */}
                <div className="bg-light p-3 text-center border-bottom" style={{ height: '110px' }}>
                  <img
                    src={routine?.training_details?.image || backendURL + appConfig?.logo}
                    className="img-fluid h-100"
                    alt="Training"
                    style={{ objectFit: 'contain' }}
                  />
                </div>

                <div className="card-body p-3">
                  <h6 className="fw-bold text-dark" style={{ minHeight: '30px', lineHeight: '' }}>
                    {routine?.training_details?.training_name}
                  </h6>

                  <div className="mb-1">
                    <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2 d-inline-flex align-items-center gap-1">
                      <MdOutlineMeetingRoom size={14} />
                      {routine?.hall_details?.hall_name}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-2 mb-1 p-1 rounded-3" style={{ background: '#f8f9fa' }}>
                    <div className="flex-fill text-center border-end">
                      <div className="text-muted d-flex align-items-center justify-content-center gap-1" style={{ fontSize: '10px' }}>
                        <MdOutlineDateRange size={12} /> START
                      </div>
                      <small className="fw-bold">{routine?.start_date}</small>
                    </div>
                    <div className="flex-fill text-center border-end">
                      <div className="text-muted d-flex align-items-center justify-content-center gap-1" style={{ fontSize: '10px' }}>
                        <MdOutlineDateRange size={12} /> END
                      </div>
                      <small className="fw-bold">{routine?.end_date}</small>
                    </div>
                    <div className="flex-fill text-center">
                      <div className="text-muted d-flex align-items-center justify-content-center gap-1" style={{ fontSize: '10px' }}>
                        <HiOutlineUsers size={12} /> TRAINEES
                      </div>
                      <small className="fw-bold">{routine?.trainee_count}</small>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-start">
                    <div className="mb-1 flex-grow-1 me-2">
                      <small className="text-muted fw-bold d-block mb-1">Coordinators</small>
                      <div style={{ maxHeight: '38px', overflowY: 'auto' }}>
                        {routine?.tr_routine_coordinator
                          ?.filter((c: ICoordinator) => !c.is_module_coordinator)
                          .map((c: ICoordinator, i: number) => (
                            <div key={i} className="small text-secondary">
                              • {c?.coordinator_details?.first_name} {c?.coordinator_details?.last_name}
                            </div>
                          ))}
                      </div>
                    </div>

                    <button
                      className="btn btn-light btn-sm rounded-circle p-2 flex-shrink-0 border"
                      onClick={() =>
                        navigate(`/routine-management/${routine?.routine_master_id}/feedback`)
                      }
                      title="Feedback"
                    >
                      <img src={feedbackIcon} alt="feedback" style={{ width: '18px', height: '18px' }} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
};

export default Dashboard;